import { HSLColor } from "../../colors/HSLColor";
import { ClassFlag } from "../../dom-tools/ClassFlag";
import { DOMHitTest } from "../../dom-tools/DOMHitTest";
import { ElementType } from "../../dom-tools/ElementType";
import { Events, OnKeyDown, OnMouseDrag } from "../../dom-tools/EventListeners";
import { Property } from "../../events/Property";
import { MathX } from "../../math/MathX";
import { DirectionFlags } from "../basics/DirectionFlags";
import { EventListenerCache } from "../basics/EventListenerCache";
import { LinearGradient } from "../basics/Gradient";
import { UIListProperty } from "../basics/UIListProperty";
import { UIProperty } from "../basics/UIProperty";
import { GraphicalElement } from "../graphical/GraphicalElement";
import { LinkedGraphicalElement } from "../graphical/LinkedGraphicalElement";
import { LinkedGraphicalElementList } from "../graphical/LinkedGraphicalElementList";
import { Rectangle } from "../graphical/Rectangle";
import { UIComponent } from "./UIComponent";

export class SplitView extends UIComponent
{
  static readonly elementType = new ElementType( "ui--split--view" );
  readonly isHorizontal = new UIProperty<boolean>( this, true );
  readonly splitSize = new UIProperty( this, 5 );
  readonly minSplitSpace = new UIProperty( this, 1 );

  static readonly viewFlag = new ClassFlag( "ui--view" );
  readonly views = new LinkedGraphicalElementList( this, SplitView.viewFlag );

  static readonly handleFlag = new ClassFlag( "ui--handle" );
  protected handles = new LinkedGraphicalElementList( this, SplitView.handleFlag );
  protected handlePositions = new UIListProperty<number>( this );

  constructor()
  {
    super();

    this._setElement( SplitView.elementType.create() );

    this._addListeners();

    this._linkRedraw( 
      this.isHorizontal, 
      this.splitSize,
      this.views,
      this.handles,
      this.handlePositions
    );

    this.isHorizontal.forceDispatch();
  }

  get neededHandles()
  {
    return Math.max( 0, this.views.length - 1 );
  }

  #slowDrag = false;
  #changeMode:(e:KeyboardEvent)=>void;

  protected _addListeners()
  {   
    this.views.addListener(
      ()=>
      {

        if ( this.handles.length !== this.neededHandles )
        {
          this._createHandles();
        }
      }
    );

    this.isHorizontal.addListener(
      ()=>
      {
        let horizontal = this.isHorizontal.value;

        let removingFlag = horizontal ? DirectionFlags.vertical : DirectionFlags.horizontal;
        let addingFlag   = horizontal ? DirectionFlags.horizontal : DirectionFlags.vertical;  
        
        removingFlag.removeFrom( this.element );
        addingFlag.set( this.element );
      }
    );

    this.handles.addCallback( 
      ( g:GraphicalElement, ev:EventListenerCache ) =>
      {
        ev.onListener( OnMouseDrag, g.element, 
        
        (e:MouseEvent )=> 
        {
          let graphics = g;
          let index = this.handles.indexOf( g );
          let splitPosition = this.handlePositions.get( index );
          
          let splitPositionStart = this.minSplitSpace.value;
          let splitPositionEnd = 100 - this.minSplitSpace.value;

          if ( index > 0 )
          {
            splitPositionStart = this.handlePositions.get( index - 1 ) + this.minSplitSpace.value;
          }

          if ( index < this.handlePositions.length )
          {
            splitPositionEnd = this.handlePositions.get( index + 1 ) - this.minSplitSpace.value;
          }
          


          let position = DOMHitTest.getPointerPosition( e );;

          if ( Events.isStart( e ) )
          {
            graphics.setDragData( e, splitPosition );
            UIComponent.lockCursor( graphics.element );
            this.#slowDrag = e.shiftKey;

            if ( ! this.#changeMode )
            {
              this.#changeMode = (e:KeyboardEvent)=>{ if ( e.shiftKey ){ this.#slowDrag = !this.#slowDrag; } }
            }

            OnKeyDown.add( document.body, this.#changeMode );
          } 

          let movement = position.sub( graphics.startPointerPosition );

          let movementValue = this.isHorizontal.value ? movement.y : movement.x;

          let normalizedChange = movementValue / this.sideLength;

          if ( this.#slowDrag )
          {
            normalizedChange /= 10;
          }

          let startValue = graphics.getDragData<number>().value;

          let newValue = ( startValue + normalizedChange * 100 );

          newValue = MathX.clamp( newValue, splitPositionStart, splitPositionEnd );

          this.handlePositions.set( index, newValue );

          Events.silent( e );
          
          if ( Events.isEnd( e ) )
          {
            UIComponent.freeCursor();
            OnKeyDown.remove( document.body, this.#changeMode );
          }

          graphics.setDragData( e, newValue );

          this._updateChildren();

        }

        );
      }

    );
  }

  protected _createHandles() 
  { 
    console.log( this.neededHandles, this.handles.length );

    while ( this.neededHandles > this.handles.length )
    {
      let rectangle = new Rectangle();
      this.handles.add( rectangle );
      this.handlePositions.add( 0 );
    }

    while ( this.neededHandles < this.handles.length )
    {
      this.handles.remove( this.handles.length - 1 );
      this.handlePositions.remove( this.handlePositions.length - 1 );
    }

    for ( let i = 0; i < this.handles.length; i++ )
    {
      this.handlePositions.set( i, 100 * ( i + 1 ) / this.views.length );
    }

    console.log( this.neededHandles, this.handles.length );

    this.redraw();

  }


  protected _update()
  {
    super._update();
    
    if ( this.views.length <= 1 )
    {
      this._redrawNoneOrOne();
    }
    else
    {
      this.handles.forAll( ( h, i ) => this._drawHandle( i ) );
      this.views.forAll( ( v, i ) => this._drawView( i ) );
    }
    
  }


  get sideLength()
  {
    return this.isHorizontal.value ? this.height : this.width;
  }

  protected _drawHandle( index:number )
  {
    let x = 0;
    let y = 0;
    let w = this.width;
    let h = this.height;

    let positionStart = this.sideLength *  this.handlePositions.get( index ) / 100;

    if ( this.isHorizontal.value )
    {
      y = positionStart - this.splitSize.value / 2;
      h = this.splitSize.value;
    }
    else
    {
      x = positionStart - this.splitSize.value / 2;
      w = this.splitSize.value;
    }

    let handle = this.handles.get( index );
    handle.dimensions.set( x, y, w, h );
  }

  protected _drawView( index:number )
  {   

    let positionStart = 0;
    let positionEnd   = this.sideLength;

    let indexStart = index - 1;
    let indexEnd   = index;
    
    


    if ( indexStart >= 0 )
    {
      positionStart = this.handlePositions.get( indexStart ) / 100 * this.sideLength;
      positionStart += this.splitSize.value / 2;
    }
    
    if ( indexEnd < this.handlePositions.length )
    {
      positionEnd = this.handlePositions.get( indexEnd ) / 100 * this.sideLength;
      positionEnd -= this.splitSize.value / 2;
    }


    let x = 0;
    let y = 0;
    let w = this.width;
    let h = this.height;

    if ( this.isHorizontal.value )
    {
      y = positionStart;
      h = positionEnd - positionStart;
    }
    else
    {
      x = positionStart;
      w = positionEnd - positionStart; 
    }

    let view = this.views.get( index );

    if ( UIComponent.debug.in( this.element ) )
    {
      console.log( 

        "own dimensions:", this.width, this.height, 
        {x, y, w, h},
        { positionStart, positionEnd },
        { side: this.sideLength }
      );
    }

    
    view.dimensions.set( x, y, w, h );
    view.redraw();
  }

  _redrawNoneOrOne()
  {
    if ( this.views.length == 0 )
    {
      return;
    }

    let g = this.views.get( 0 );
    g.dimensions.setFrom( this.dimensions );
  }

  
}